/**
 * Phase 3 — reminders calendar: month grid (react-day-picker 8, already a dependency) with a list toggle.
 * Each reminder links to its deal. Reads /api/reminders for the visible month (own reminders; admins can
 * flip to everyone's). Done / dismiss act in place.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DayPicker } from 'react-day-picker';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Check, ChevronLeft, ChevronRight, List, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { bucketOf, dayKey, daysFromToday, dealPath, fmtDue, groupByDay, monthRange, sortForList } from './reminderModel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE = { overdue: 'bg-red-500', today: 'bg-amber-500', upcoming: 'bg-blue-500' };

const ReminderCalendar = ({ getAuthHeaders, isAdmin, onCountsChange }) => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [view, setView] = useState('month');
  const [everyone, setEveryone] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // list view: everything still pending plus this month's closed ones; month view: the visible month only
      const { from, to } = monthRange(month.getFullYear(), month.getMonth());
      const params = view === 'month'
        ? { from, to, status: 'all', mine: !(isAdmin && everyone) }
        : { status: 'pending', mine: !(isAdmin && everyone) };
      const res = await axios.get(`${API}/reminders`, { headers: getAuthHeaders(), params });
      setRows(res.data?.reminders || []);
    } catch (e) {
      toast.error('Could not load reminders');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, isAdmin, everyone, month, view]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (onCountsChange) onCountsChange(rows); }, [rows, onCountsChange]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/reminders/${id}`, { status }, { headers: getAuthHeaders() });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update reminder');
    }
  };

  const pending = useMemo(() => rows.filter((r) => r.status === 'pending'), [rows]);
  const byDay = useMemo(() => groupByDay(pending), [pending]);
  const selectedKey = selected ? dayKey(selected) : null;
  const dayList = selectedKey ? (byDay[selectedKey] || []) : [];

  const Row = ({ r, showDate = true }) => {
    const bucket = bucketOf(r);
    const days = daysFromToday(r);
    const path = dealPath(r);
    return (
      <li className={`flex items-start justify-between gap-3 py-2 ${bucket === 'overdue' ? 'bg-red-50 -mx-2 px-2 rounded' : ''}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs">
            <span className={`inline-block h-2 w-2 rounded-full ${TONE[bucket] || 'bg-slate-300'}`} />
            {showDate && <span className={`tabular-nums ${bucket === 'overdue' ? 'text-red-700 font-semibold' : 'text-slate-700'}`}>{fmtDue(r.due_at)}</span>}
            {!showDate && <span className="tabular-nums text-slate-700">{new Date(r.due_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span>}
            {bucket === 'overdue' && <span className="text-[11px] text-red-700">{days}d overdue</span>}
            {path ? (
              <button type="button" className="text-blue-700 hover:underline font-medium" onClick={() => navigate(path)}>
                #{r.deal_id_number || 'deal'}
              </button>
            ) : <span className="text-slate-400">general</span>}
          </div>
          <div className="text-sm text-slate-800 mt-0.5 break-words">{r.note || <span className="text-slate-400">(no note)</span>}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" title="Done" onClick={() => setStatus(r.id, 'done')}><Check className="h-4 w-4 text-emerald-600" /></Button>
          <Button size="sm" variant="ghost" title="Dismiss" onClick={() => setStatus(r.id, 'dismissed')}><X className="h-4 w-4 text-slate-500" /></Button>
        </div>
      </li>
    );
  };

  const DayContent = ({ date }) => {
    const items = byDay[dayKey(date)] || [];
    const worst = items.some((r) => bucketOf(r) === 'overdue') ? 'overdue' : items.some((r) => bucketOf(r) === 'today') ? 'today' : items.length ? 'upcoming' : null;
    return (
      <div className="flex flex-col items-center leading-none">
        <span>{date.getDate()}</span>
        {items.length > 0 && (
          <span className={`mt-0.5 text-[10px] px-1 rounded-full text-white ${TONE[worst]}`} data-testid="reminder-day-badge">{items.length}</span>
        )}
      </div>
    );
  };

  return (
    <Card className="p-4" data-testid="reminder-calendar">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1">
          <Button size="sm" variant={view === 'month' ? 'default' : 'outline'} onClick={() => setView('month')}><CalendarDays className="h-4 w-4 mr-1" />Month</Button>
          <Button size="sm" variant={view === 'list' ? 'default' : 'outline'} onClick={() => setView('list')}><List className="h-4 w-4 mr-1" />List</Button>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} /> everyone's
            </label>
          )}
          {view === 'month' && (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => { setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1)); setSelected(null); }}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm font-medium w-[130px] text-center">{month.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</span>
              <Button size="sm" variant="ghost" onClick={() => { setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1)); setSelected(null); }}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
      </div>

      {loading && <div className="text-xs text-slate-400 mb-2">Loading…</div>}

      {view === 'month' ? (
        <div className="grid grid-cols-1 lg:grid-cols-[auto,1fr] gap-4">
          <DayPicker
            mode="single"
            month={month}
            onMonthChange={(m) => { setMonth(new Date(m.getFullYear(), m.getMonth(), 1)); setSelected(null); }}
            selected={selected || undefined}
            onSelect={(d) => setSelected(d || null)}
            showOutsideDays
            components={{ DayContent }}
            classNames={{
              months: 'flex', month: 'space-y-3', caption: 'hidden', nav: 'hidden',
              table: 'w-full border-collapse', head_row: 'flex', head_cell: 'text-slate-500 w-12 font-normal text-[0.75rem]',
              row: 'flex w-full mt-1', cell: 'relative p-0 text-center text-sm w-12 h-12',
              day: 'h-12 w-12 rounded-md hover:bg-slate-100 aria-selected:bg-slate-900 aria-selected:text-white',
              day_today: 'ring-1 ring-amber-400', day_outside: 'text-slate-300',
            }}
          />
          <div>
            <div className="text-xs text-slate-500 mb-1">
              {selected ? selected.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : 'Pick a day to see its reminders'}
            </div>
            {selected && dayList.length === 0 && <div className="text-xs text-slate-400">Nothing due that day.</div>}
            {dayList.length > 0 && <ul className="divide-y divide-slate-100">{dayList.map((r) => <Row key={r.id} r={r} showDate={false} />)}</ul>}
            {!selected && pending.length > 0 && (
              <div className="text-xs text-slate-500 mt-2">{pending.length} pending this month.</div>
            )}
          </div>
        </div>
      ) : (
        pending.length === 0 ? (
          <div className="text-xs text-slate-400">No pending reminders.</div>
        ) : (
          <ul className="divide-y divide-slate-100">{sortForList(pending).map((r) => <Row key={r.id} r={r} />)}</ul>
        )
      )}
    </Card>
  );
};

export default ReminderCalendar;
